import { isAllowedCountryCode } from '@artem-lex/common-server/countries'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ALL_COUNTRIES } from '@/shared/constants/countries'
import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import { useCurrentUser } from '@/shared/hooks/api/user'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PopoverCombobox } from '@/components/ui/popover-combobox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { applyApiValidationErrorsToForm } from '@/shared/utils/applyApiValidationErrorsToForm'

const MAX_FIELD_LENGTH = 255

const ADDRESS_ONLY_ALLOWED_FIELDS = new Set([
  'street',
  'streetNumber',
  'city',
  'country',
  'postCode',
])

const WECHAT_ADDRESS_ALLOWED_FIELDS = new Set([
  'firstName',
  'lastName',
  'organizationName',
  'contactPhoneNumber',
  ...ADDRESS_ONLY_ALLOWED_FIELDS,
])

function buildRequiredTextField(
  translate: (key: string, options?: Record<string, unknown>) => string,
  maxLength: number = MAX_FIELD_LENGTH
) {
  return z
    .string()
    .trim()
    .min(1, translate('validation.required'))
    .max(maxLength, translate('validation.maxLength', { max: maxLength }))
}

function buildAddressFields(
  translate: (key: string, options?: Record<string, unknown>) => string
) {
  return {
    street: buildRequiredTextField(translate),
    streetNumber: buildRequiredTextField(translate),
    city: buildRequiredTextField(translate),
    country: z
      .string()
      .trim()
      .min(1, translate('validation.countryRequired'))
      .refine((value) => value.length === 2, {
        message: translate('validation.countryCodeLength'),
      })
      .refine(isAllowedCountryCode, {
        message: translate('validation.countryCodeUnsupported'),
      }),
    postCode: buildRequiredTextField(translate),
  }
}

function createAddressOnlySchema(
  translate: (key: string, options?: Record<string, unknown>) => string
) {
  return z.object(buildAddressFields(translate))
}

function createWeChatAddressSchema(
  translate: (key: string, options?: Record<string, unknown>) => string
) {
  return z.object({
    firstName: buildRequiredTextField(translate),
    lastName: buildRequiredTextField(translate),
    organizationName: buildRequiredTextField(translate),
    contactPhoneNumber: buildRequiredTextField(translate),
    ...buildAddressFields(translate),
  })
}

export type AddressOnlyFormValues = z.infer<
  ReturnType<typeof createAddressOnlySchema>
>
export type WeChatAddressFormValues = z.infer<
  ReturnType<typeof createWeChatAddressSchema>
>
export type AddressFormValues = AddressOnlyFormValues | WeChatAddressFormValues

export function useOnboardingAddressSchema(includeWeChatProfileFields: true): ReturnType<
  typeof createWeChatAddressSchema
>
export function useOnboardingAddressSchema(includeWeChatProfileFields: false): ReturnType<
  typeof createAddressOnlySchema
>
export function useOnboardingAddressSchema(includeWeChatProfileFields: boolean) {
  const { t } = useTranslation('onboarding')
  return useMemo(() => {
    if (includeWeChatProfileFields) {
      return createWeChatAddressSchema(t)
    }
    return createAddressOnlySchema(t)
  }, [includeWeChatProfileFields, t])
}

type OnboardingAddressStepProps = {
  includeWeChatProfileFields: boolean
  isBusy: boolean
  onSubmit: (data: AddressFormValues) => void | Promise<void>
}

type OnboardingAddressOnlyStepProps = {
  isBusy: boolean
  onSubmit: (data: AddressOnlyFormValues) => void | Promise<void>
}

function OnboardingAddressOnlyStep({
  isBusy,
  onSubmit,
}: OnboardingAddressOnlyStepProps) {
  const { t } = useTranslation(['common', 'countries', 'onboarding'])
  const addressSchema = useOnboardingAddressSchema(false)
  const { data: organization } = useCurrentOrganization()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<AddressOnlyFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      street: '',
      streetNumber: '',
      city: '',
      country: '',
      postCode: '',
    },
  })

  useEffect(() => {
    if (!organization) {
      return
    }

    form.reset({
      street: organization.street ?? '',
      streetNumber: organization.streetNumber ?? '',
      city: organization.city ?? '',
      country: (organization.country ?? '').toLowerCase(),
      postCode: organization.postCode ?? '',
    })
  }, [form, organization])

  async function handleSubmit(data: AddressOnlyFormValues) {
    setSubmitError(null)
    try {
      await onSubmit(data)
    } catch (error) {
      const { appliedFieldErrors, formLevelMessage } =
        applyApiValidationErrorsToForm(
          form,
          error,
          ADDRESS_ONLY_ALLOWED_FIELDS
        )
      if (appliedFieldErrors === 0) {
        setSubmitError(formLevelMessage ?? t('addressSubmit.genericError'))
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent>
            <div className='space-y-4 pb-4'>
              {submitError && (
                <Alert variant='destructive'>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
              <FormField
                control={form.control}
                name='street'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.streetLabel', { ns: 'common' })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                        autoComplete='street-address'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='streetNumber'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.streetNumberLabel', {
                        ns: 'common',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='grid grid-cols-2 items-start gap-4'>
                <FormField
                  control={form.control}
                  name='postCode'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('settingsOrganization.postCodeLabel', {
                          ns: 'common',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='postal-code'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='city'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('settingsOrganization.cityLabel', { ns: 'common' })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='address-level2'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name='country'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.countryLabel', { ns: 'common' })}
                    </FormLabel>
                    <FormControl>
                      <PopoverCombobox
                        id='onboarding-country'
                        items={ALL_COUNTRIES}
                        value={field.value ? field.value : null}
                        onValueChange={(code) => field.onChange(code ?? '')}
                        getItemLabel={(code) =>
                          t(code, { ns: 'countries' }) || code
                        }
                        getItemKey={(code) => code}
                        placeholder={t(
                          'settingsOrganization.countryPlaceholder',
                          {
                            ns: 'common',
                          }
                        )}
                        searchPlaceholder={t(
                          'settingsOrganization.countrySearchPlaceholder',
                          { ns: 'common' }
                        )}
                        emptyText={t('settingsOrganization.countryEmpty', {
                          ns: 'common',
                        })}
                        disabled={isBusy}
                        showClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        <div className='flex justify-end pt-6'>
          <Button type='submit' disabled={isBusy}>
            {t('continue', { ns: 'onboarding' })}
          </Button>
        </div>
      </form>
    </Form>
  )
}

type OnboardingWeChatAddressStepProps = {
  isBusy: boolean
  onSubmit: (data: WeChatAddressFormValues) => void | Promise<void>
}

function OnboardingWeChatAddressStep({
  isBusy,
  onSubmit,
}: OnboardingWeChatAddressStepProps) {
  const { t } = useTranslation(['common', 'countries', 'onboarding'])
  const addressSchema = useOnboardingAddressSchema(true)
  const { data: organization } = useCurrentOrganization()
  const { data: currentUser } = useCurrentUser()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<WeChatAddressFormValues>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      organizationName: '',
      contactPhoneNumber: '',
      street: '',
      streetNumber: '',
      city: '',
      country: '',
      postCode: '',
    },
  })

  useEffect(() => {
    if (!organization) {
      return
    }

    form.reset({
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      organizationName: organization.name ?? '',
      contactPhoneNumber: organization.contactPhoneNumber ?? '',
      street: organization.street ?? '',
      streetNumber: organization.streetNumber ?? '',
      city: organization.city ?? '',
      country: (organization.country ?? '').toLowerCase(),
      postCode: organization.postCode ?? '',
    })
  }, [currentUser, form, organization])

  async function handleSubmit(data: WeChatAddressFormValues) {
    setSubmitError(null)
    try {
      await onSubmit(data)
    } catch (error) {
      const { appliedFieldErrors, formLevelMessage } =
        applyApiValidationErrorsToForm(
          form,
          error,
          WECHAT_ADDRESS_ALLOWED_FIELDS
        )
      if (appliedFieldErrors === 0) {
        setSubmitError(formLevelMessage ?? t('addressSubmit.genericError'))
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent>
            <div className='space-y-4 pb-4'>
              {submitError && (
                <Alert variant='destructive'>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
              <div className='grid grid-cols-2 items-start gap-4'>
                <FormField
                  control={form.control}
                  name='firstName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('wechatProfile.firstNameLabel', { ns: 'onboarding' })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='given-name'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='lastName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('wechatProfile.lastNameLabel', { ns: 'onboarding' })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='family-name'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name='organizationName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('wechatProfile.organizationNameLabel', {
                        ns: 'onboarding',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                        autoComplete='organization'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='contactPhoneNumber'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('wechatProfile.contactPhoneLabel', { ns: 'onboarding' })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                        autoComplete='tel'
                        type='tel'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='street'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.streetLabel', { ns: 'common' })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                        autoComplete='street-address'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='streetNumber'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.streetNumberLabel', {
                        ns: 'common',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        disabled={isBusy}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='grid grid-cols-2 items-start gap-4'>
                <FormField
                  control={form.control}
                  name='postCode'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('settingsOrganization.postCodeLabel', {
                          ns: 'common',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='postal-code'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='city'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('settingsOrganization.cityLabel', { ns: 'common' })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          disabled={isBusy}
                          autoComplete='address-level2'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name='country'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('settingsOrganization.countryLabel', { ns: 'common' })}
                    </FormLabel>
                    <FormControl>
                      <PopoverCombobox
                        id='onboarding-country'
                        items={ALL_COUNTRIES}
                        value={field.value ? field.value : null}
                        onValueChange={(code) => field.onChange(code ?? '')}
                        getItemLabel={(code) =>
                          t(code, { ns: 'countries' }) || code
                        }
                        getItemKey={(code) => code}
                        placeholder={t(
                          'settingsOrganization.countryPlaceholder',
                          {
                            ns: 'common',
                          }
                        )}
                        searchPlaceholder={t(
                          'settingsOrganization.countrySearchPlaceholder',
                          { ns: 'common' }
                        )}
                        emptyText={t('settingsOrganization.countryEmpty', {
                          ns: 'common',
                        })}
                        disabled={isBusy}
                        showClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        <div className='flex justify-end pt-6'>
          <Button type='submit' disabled={isBusy}>
            {t('continue', { ns: 'onboarding' })}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export function OnboardingAddressStep({
  includeWeChatProfileFields,
  isBusy,
  onSubmit,
}: OnboardingAddressStepProps) {
  if (includeWeChatProfileFields) {
    return (
      <OnboardingWeChatAddressStep
        isBusy={isBusy}
        onSubmit={onSubmit}
      />
    )
  }

  return (
    <OnboardingAddressOnlyStep
      isBusy={isBusy}
      onSubmit={onSubmit}
    />
  )
}
